import { callOutcomeExistsDetailsSchema, type CallOutcomeExistsDetails, type CallView, type SaveOutcomeResponse } from "@gioia365/contracts";
import { ApiRequestError } from "../api/http";
import { isTransientError } from "../api/queries";

// Il salvataggio dell'esito con i nuovi tentativi (piano M4, Task 13; questione 161): la stessa richiesta, con la stessa
// chiave del browser, si rimanda dopo un guasto passeggero (la rete, un 5xx) finché la pagina è aperta, con attese che
// crescono fino a 15 secondi. Nessuna coda durevole nel browser in M4: chiusa la pagina, la chiamata resta senza esito e
// la scheda la ripropone («Manca l'esito…»).

/** Le attese fra un tentativo e l'altro dopo un guasto passeggero; poi ogni 15 secondi. */
export const OUTCOME_RETRY_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000, 15_000] as const;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export type OutcomeResult = { kind: "saved"; call: CallView } | { kind: "late"; existing: CallOutcomeExistsDetails | null };

/**
 * Manda l'esito finché il server lo conferma (`saved`) o dice che la chiamata ne aveva già uno (`late`, con quello che
 * resta). Dopo un guasto passeggero chiama `retrying` e riprova, finché `wanted()` è vero (la pagina aperta, la stessa
 * chiamata); un rifiuto, o un guasto quando non serve più, si lancia.
 */
export async function sendOutcome(options: { send(): Promise<SaveOutcomeResponse>; retrying(): void; wanted(): boolean }): Promise<OutcomeResult> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return { kind: "saved", call: (await options.send()).call };
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "call_outcome_exists") {
        const existing = callOutcomeExistsDetailsSchema.safeParse(error.details);
        return { kind: "late", existing: existing.success ? existing.data : null };
      }
      if (!isTransientError(error) || !options.wanted()) throw error;
      options.retrying();
      await wait(OUTCOME_RETRY_DELAYS_MS[Math.min(attempt, OUTCOME_RETRY_DELAYS_MS.length - 1)] ?? 15_000);
      if (!options.wanted()) throw error;
    }
  }
}
